import React, { useEffect, useMemo, useState } from 'react';
import { onValue, push, ref, remove, set } from 'firebase/database';
import { toast } from 'react-toastify';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';

import { db } from '../../firebase';

// NotesTab — free-form internal notes attached to a device.
//
// Stored at mdm/devices/<MAC>/notes/<pushKey>:  { text, timestamp }.
// Only authenticated dashboard users see/edit notes; the device itself
// never reads them.

export default function NotesTab({ mac }) {
    const [notes, setNotes] = useState({});
    const [draft, setDraft] = useState('');
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        if (!mac) return undefined;
        const notesRef = ref(db, `mdm/devices/${mac}/notes`);
        const unsub = onValue(notesRef, (snap) => setNotes(snap.val() || {}));
        return () => unsub();
    }, [mac]);

    const sorted = useMemo(() => {
        return Object.entries(notes)
            .map(([id, n]) => ({ id, ...n }))
            .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    }, [notes]);

    const addNote = async () => {
        const text = draft.trim();
        if (!text) return;
        try {
            setAdding(true);
            const notesRef = ref(db, `mdm/devices/${mac}/notes`);
            const newRef = push(notesRef);
            await set(newRef, { text, timestamp: Date.now() });
            setDraft('');
        } catch (err) {
            toast.error(`Failed to add note: ${err.message}`);
        } finally {
            setAdding(false);
        }
    };

    const deleteNote = async (id) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            await remove(ref(db, `mdm/devices/${mac}/notes/${id}`));
        } catch (err) {
            toast.error(`Failed to delete note: ${err.message}`);
        }
    };

    return (
        <section className="space-y-6">
            {/* Composer */}
            <div className="card-chrome p-5">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2 mb-3">
                    <FiEdit2 /> Add a note
                </h2>
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="What should the next operator know about this device?"
                    rows={3}
                    className="w-full bg-surface-inset border border-white/[0.06] rounded-lg px-4 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand/40 resize-none"
                />
                <div className="mt-2 flex justify-end">
                    <button
                        onClick={addNote}
                        disabled={!draft.trim() || adding}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-brand hover:bg-brand-400 text-white text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <FiPlus /> {adding ? 'Adding…' : 'Add note'}
                    </button>
                </div>
            </div>

            {/* List */}
            {sorted.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/[0.08] p-10 text-center text-gray-500">
                    No notes yet.
                </div>
            ) : (
                <ul className="space-y-3">
                    {sorted.map((note) => (
                        <li
                            key={note.id}
                            className="card-chrome p-4"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <p className="text-sm text-gray-200 whitespace-pre-wrap break-words flex-1 min-w-0">
                                    {note.text}
                                </p>
                                <button
                                    onClick={() => deleteNote(note.id)}
                                    className="text-gray-500 hover:text-red-400 p-1 rounded transition"
                                    title="Delete note"
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                {note.timestamp ? new Date(note.timestamp).toLocaleString() : ''}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
